import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Pads a composited DesignHuddle product preview (garment photo + logo) onto a fixed white
 * square canvas so every product's persisted thumbnail shares identical final pixel dimensions.
 *
 * WHY: DH's product_image compositing has no fixed-output-canvas option — the rendered image's
 * overall width/height always follows the source garment photo's own aspect ratio scaled to
 * product_image_width (confirmed via product-edit-modal.component.ts's canvasZoneMaxHeight(),
 * which derives height from `500 * previewAspectRatio` since "the product image may not be
 * square"). Since different templates/garment photos (t-shirt vs hoodie vs mug, etc.) have
 * different native aspect ratios, their final thumbnails came out at different pixel dimensions
 * — which, sent straight through to Shopify, made product-listing grid rows look asymmetric
 * (some products rendering larger, some smaller). The old InkSoft pipeline avoided this by
 * always normalizing to one fixed canvas.
 *
 * Operates on an already-fetched Blob (the caller already has the bytes on hand for uploading to
 * Azure) rather than re-fetching a URL, and uses an object URL so the canvas is never
 * CORS-tainted. Fails soft (image load error, canvas error) by resolving with the original blob,
 * so a normalize failure never blocks the upload.
 */
@Injectable({
  providedIn: 'root'
})
export class ProductPreviewNormalizeService {

  normalizeToSquare(blob: Blob, targetSize: number = 1000): Observable<Blob> {
    return new Observable<Blob>(subscriber => {
      const objectUrl = URL.createObjectURL(blob);
      const cleanup = () => URL.revokeObjectURL(objectUrl);

      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('2d canvas context unavailable');

          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, targetSize, targetSize);

          const srcW = img.naturalWidth || targetSize;
          const srcH = img.naturalHeight || targetSize;
          // "Contain" fit: scale to fit entirely within the square without cropping, then
          // center — preserves the garment photo's proportions instead of stretching/distorting it.
          const scale = Math.min(targetSize / srcW, targetSize / srcH);
          const drawW = srcW * scale;
          const drawH = srcH * scale;
          const dx = (targetSize - drawW) / 2;
          const dy = (targetSize - drawH) / 2;
          ctx.drawImage(img, dx, dy, drawW, drawH);

          canvas.toBlob(normalizedBlob => {
            cleanup();
            subscriber.next(normalizedBlob || blob);
            subscriber.complete();
          }, 'image/png');
        } catch (e) {
          cleanup();
          console.warn('⚠️ Square-normalize canvas draw failed, using un-normalized image:', e);
          subscriber.next(blob);
          subscriber.complete();
        }
      };
      img.onerror = () => {
        cleanup();
        console.warn('⚠️ Square-normalize image load failed, using un-normalized image');
        subscriber.next(blob);
        subscriber.complete();
      };
      img.src = objectUrl;
    });
  }
}
